import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Checkbox, Col, Empty, Form, Input, Modal, Popconfirm, Row, Select, Skeleton, Space, Tag, Tooltip, Typography, message } from "antd";
import { DeleteOutlined, EditOutlined, ExceptionOutlined, FilePdfOutlined, FilterOutlined, HolderOutlined, PlusOutlined, ScheduleOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import MUIDataTable from "mui-datatables";
import { useDrag, useDrop } from "react-dnd";
import { BodyComponent, CreateButton, FilterComponent, HeaderComponent } from "@comps/components";
import { makeRequestStateless } from "@utils/helpers";
import {
  createDirectiveFilterReq,
  deleteDirectiveFilterReq,
  downloadReportReq,
  generateReportReq,
  getFilterReq,
  getInitialDataReq,
  updateDirectiveFilterReq
} from "../requests";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { clearFilterOptions } from "../../../../redux/filterOptionsSlice";
import ScheduleReport from "./ScheduleReport";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { reportTooltips } from "../../common/reportTooltips";

const fieldLabel = makeFieldLabel(reportTooltips.report, { namespace: "report" });

const GenerateClassBasedReport = (props) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authUser = useSelector((state) => state.auth.authUser);

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [filterFields, setFilterFields] = useState([]);
  const [filterApiPayload, setFilterApiPayload] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState(null);
  const [columnSearch, setColumnSearch] = useState("");
  const [selectedDownloadColumns, setSelectedDownloadColumns] = useState([]);
  const [directiveFilters, setDirectiveFilters] = useState([]);
  const [directiveFilterModalOpen, setDirectiveFilterModalOpen] = useState(false);
  const [editingDirectiveFilter, setEditingDirectiveFilter] = useState(null);
  const [directiveFilterSaving, setDirectiveFilterSaving] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [directiveFilterForm] = Form.useForm();
  const columnChoiceListRef = useRef(null);
  const columnChoiceScrollFrameRef = useRef(null);
  const columnChoiceScrollDirectionRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    loadReportInitialData();
    // Load once per report id; the callback updates local report/filter/preview state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => () => {
    if (columnChoiceScrollFrameRef.current) {
      cancelAnimationFrame(columnChoiceScrollFrameRef.current);
      columnChoiceScrollFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!columnPickerOpen && columnChoiceScrollFrameRef.current) {
      cancelAnimationFrame(columnChoiceScrollFrameRef.current);
      columnChoiceScrollFrameRef.current = null;
      columnChoiceScrollDirectionRef.current = null;
    }
  }, [columnPickerOpen]);

  const hasDirectiveFilters = useMemo(() => filterFields.length > 0, [filterFields]);
  const filteredColumnChoices = useMemo(() => {
    const search = columnSearch.trim().toLowerCase();

    if (!search) {
      return previewColumns;
    }

    return previewColumns.filter((column) =>
      `${column.label} ${column.name}`.toLowerCase().includes(search)
    );
  }, [columnSearch, previewColumns]);
  const visiblePreviewColumns = useMemo(() => {
    if (selectedDownloadColumns.length === 0) {
      return [];
    }

    const selectedColumnsMap = previewColumns.reduce((acc, column) => {
      acc[column.name] = column;
      return acc;
    }, {});

    return previewColumns
      .filter((column) => selectedDownloadColumns.includes(column.name))
      .map((column) => selectedColumnsMap[column.name] || column);
  }, [previewColumns, selectedDownloadColumns]);
  const visiblePreviewData = useMemo(() => {
    if (visiblePreviewColumns.length === 0 || !Array.isArray(previewData)) {
      return [];
    }

    if (previewData.every((row) => Array.isArray(row))) {
      const selectedIndexes = visiblePreviewColumns.map((column) =>
        previewColumns.findIndex((previewColumn) => previewColumn.name === column.name)
      );

      return previewData.map((row) => selectedIndexes.map((index) => row[index]));
    }

    if (previewData.every((row) => row && typeof row === "object" && !Array.isArray(row))) {
      return previewData.map((row) =>
        visiblePreviewColumns.reduce((acc, column) => {
          acc[column.name] = row[column.name];
          return acc;
        }, {})
      );
    }

    return previewData;
  }, [previewColumns, previewData, visiblePreviewColumns]);

  const loadReportInitialData = () => {
    setLoading(true);
    makeRequestStateless(getInitialDataReq, { id }, onLoadInitialSuccess, onLoadInitialError);
  };

  const onLoadInitialSuccess = (data) => {
    setReport(data.report);

    const preparedFilters = [];
    let directiveFilters = "";
    let tempGroup = "";

    const reportDirectiveFilters = data?.report?.directive_filters || data?.report?.directiveFilters || [];
    setDirectiveFilters(reportDirectiveFilters);

    reportDirectiveFilters.forEach((each) => {
      let urlDataKey = each.name;
      let optionDataKey = each.name;

      if (each.name === "employees" || each.name === "employee") {
        urlDataKey = "employments";
        optionDataKey = urlDataKey;
      }

      if (each.group !== tempGroup) {
        directiveFilters += `${each.group}_|${urlDataKey};`;
      } else {
        const lastIndex = directiveFilters.lastIndexOf(";");
        if (lastIndex === directiveFilters.length - 1) {
          directiveFilters = directiveFilters.slice(0, lastIndex);
        }
        directiveFilters += `,${urlDataKey};`;
      }

      tempGroup = each.group;

      let fieldType = "select";
      const dataKey = each.group.replace("fields", "data");
      if (each.group === "date_time_fields") {
        fieldType = "date";
      }

      let config = each.config;
      if (isValidJSON(config)) {
        config = JSON.parse(config);
      }

      preparedFilters.push({
        type: fieldType,
        placeholder: capitalizeString(each.name),
        key: each.name,
        data_key: `${dataKey}.${optionDataKey}`,
        multiple: !!config?.multiple,
        required: config?.required,
        resource_type: "employment"
      });
    });

    setFilterFields(preparedFilters);
    setFilterApiPayload({
      page: 1,
      filters: arrangeItems(directiveFilters)
    });
    setShowFilters(true);
    setLoading(false);
    loadPreviewData("");
  };

  const onLoadInitialError = () => {
    message.error("Unable to load report filters.");
    setLoading(false);
  };

  const openFilters = () => {
    //dispatch(clearFilterOptions());
    setShowFilters(true);
  };

  const openDirectiveFilterManager = () => {
    setDirectiveFilterModalOpen(true);
    startCreateDirectiveFilter();
  };

  const startCreateDirectiveFilter = () => {
    setEditingDirectiveFilter(null);
    directiveFilterForm.setFieldsValue({
      group: "date_time_fields",
      name: "from",
      enable: true,
      required: false,
      multiple: false
    });
  };

  const startEditDirectiveFilter = (filter) => {
    const config = normalizeDirectiveConfig(filter.config);
    setEditingDirectiveFilter(filter);
    directiveFilterForm.setFieldsValue({
      group: filter.group,
      name: filter.name,
      enable: config.enable !== false,
      required: !!config.required,
      multiple: !!config.multiple
    });
  };

  const submitDirectiveFilter = async () => {
    try {
      const values = await directiveFilterForm.validateFields();
      setDirectiveFilterSaving(true);

      const payload = {
        id: editingDirectiveFilter?.id,
        report_id: report?.id || id,
        group: values.group,
        name: values.name,
        config: {
          enable: values.enable !== false,
          required: !!values.required,
          multiple: !!values.multiple
        }
      };

      const request = editingDirectiveFilter ? updateDirectiveFilterReq : createDirectiveFilterReq;
      makeRequestStateless(request, payload, onDirectiveFilterSaved, onDirectiveFilterError);
    } catch (error) {
      setDirectiveFilterSaving(false);
    }
  };

  const onDirectiveFilterSaved = () => {
    message.success(editingDirectiveFilter ? "Directive filter updated." : "Directive filter added.");
    setDirectiveFilterSaving(false);
    setEditingDirectiveFilter(null);
    setSelectedFilters("");
    directiveFilterForm.resetFields();
    loadReportInitialData();
  };

  const onDirectiveFilterError = () => {
    message.error("Unable to save directive filter.");
    setDirectiveFilterSaving(false);
  };

  const deleteDirectiveFilter = (filter) => {
    makeRequestStateless(deleteDirectiveFilterReq, { id: filter.id }, () => {
      message.success("Directive filter removed.");
      setSelectedFilters("");
      loadReportInitialData();
    }, () => {
      message.error("Unable to remove directive filter.");
    });
  };

  const closeFilters = () => {
    setShowFilters(false);
  };

  const handleFilterChange = (filters) => {
    setSelectedFilters(filters);
    //setShowFilters(false);
    loadPreviewData(filters);
  };

  const loadPreviewData = (filtersString) => {
    setPreviewLoading(true);
    const payload = {
      id,
      page: 1,
      preview_call: true,
      per_page: 10,
      print_call: false,
      pagination_options: {
        pageNumber: 1,
        pageSize: 10,
        sort: ""
      },
      filters: convertFilterStringToObject(filtersString)
    };

    makeRequestStateless(generateReportReq, payload, onLoadPreviewSuccess, onLoadPreviewError);
  };

  const onLoadPreviewSuccess = (response) => {
    const columns = (response?.columns || []).map((column) => ({
      name: column.column_key,
      label: column.report_display_name || column.display_name || column.column_key,
      options: {
        filter: true,
        sort: true
      }
    }));

    const rows = response?.report_data?.data === undefined ? (response?.report_data || []) : response.report_data.data;
    const previousColumnNames = previewColumns.map((column) => column.name);

    setPreviewColumns(columns);
    setPreviewData(filterEmptyTableRows(rows));
    setSelectedDownloadColumns((prev) => {
      const nextColumnNames = columns.map((column) => column.name);
      const hadAllPreviousColumns = previousColumnNames.length === 0
        || (
          prev.length === previousColumnNames.length
          && previousColumnNames.every((columnName) => prev.includes(columnName))
        );

      if (hadAllPreviousColumns) {
        return nextColumnNames;
      }

      const preservedSelection = prev.filter((columnName) => nextColumnNames.includes(columnName));

      return preservedSelection.length > 0 ? preservedSelection : nextColumnNames;
    });
    setPreviewLoading(false);
  };

  const onLoadPreviewError = async (err) => {
    setPreviewLoading(false);

    if (err instanceof Blob) {
      try {
        const text = await err.text();
        const json = JSON.parse(text);
        message.error(json?.message || "Unable to load report data.");
        return;
      } catch (error) {
        message.error("Unable to load report data.");
        return;
      }
    }

    message.error("Unable to load report data.");
  };

  const tableOptions = {
    draggable: true,
    draggableColumns: { enabled: true },
    responsive: "standard",
    searchAlwaysOpen: true,
    filterType: "multiselect",
    filter: false,
    download: false,
    print: false,
    viewColumns: false,
    search: false,
    selectableRows: "none",
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
    onColumnViewChange: (element, action) => {
      if (action === "add" && !selectedColumns.includes(element)) {
        setSelectedColumns((prev) => [...prev, element]);
      }

      if (action === "remove" && selectedColumns.includes(element)) {
        setSelectedColumns((prev) => prev.filter((item) => item !== element));
      }
    },
    onFilterChange: (changedColumn, filterListState) => {
      setFilterList(filterListState);
    },
    downloadOptions: {
      filename: `${report ? report.title : "report"}-${dayjs().format("YYYY-MM-DD")}.xlsx`,
      separator: ","
    },
    onDownload: (buildHead, buildBody, columns, allData) => {
      const filteredData = allData.filter((row) => {
        let matched = true;
        for (let i = 0; i <= columns.length; i += 1) {
          if (filterList[i] && filterList[i].length) {
            matched = matched && filterList[i].filter((fil) => row.data[i] === fil).length > 0;
          }
        }
        return matched;
      });

      const dataForExport = [
        columns.map((col) => col.label),
        ...filteredData.map((row) => row.data.map((cell) => cell))
      ];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(dataForExport);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      XLSX.writeFile(wb, `${report ? report.title : "report"}.xlsx`);
      return false;
    },
    textLabels: {
      body: {
        noMatch: previewLoading ? "Loading preview..." : "No preview data available."
      }
    }
  };

  const openColumnPicker = (format) => {
    if (!report) {
      return;
    }

    if (!hasRequiredFilterValues()) {
      return;
    }

    if (!previewColumns.length) {
      downloadClassBasedReport(format);
      return;
    }

    setPendingDownloadFormat(format);
    setColumnPickerOpen(true);
  };

  const downloadClassBasedReport = (format) => {
    if (!report) {
      return;
    }

    if (!hasRequiredFilterValues()) {
      return;
    }

    const exportColumns = selectedDownloadColumns.length > 0
      ? previewColumns
        .filter((column) => selectedDownloadColumns.includes(column.name))
        .map((column) => column.name)
      : previewColumns.map((column) => column.name);

    setDownloadingFormat(format);
    setColumnPickerOpen(false);
    const selectedColumnsFilter = exportColumns.length
      ? `selected_columns_|${exportColumns.join(",")};`
      : "";
    const payload = {
      page: 1,
      filters: `${selectedFilters}${selectedColumnsFilter}report_id_|${report.id},user|${authUser?.employment_id};format_|${format}`,
      employment_id: authUser?.employment_id,
      format: format,
      selected_columns: exportColumns.join(",")
    };

    makeRequestStateless(downloadReportReq, payload, (res) => onDownloadSuccess(res, format), onDownloadError);
  };

  const scheduleCurrentReport = () => {
    if (!report) {
      return;
    }

    setScheduleModal(
      <ScheduleReport
        record={ report }
        initialFilters={ convertFilterStringToObject(selectedFilters) }
        scope={ location.state?.scope }
        onCompleted={ () => setScheduleModal(null) }
      />
    );
  };

  const hasRequiredFilterValues = () => {
    const requiredFilters = filterFields.filter((filter) => filter.required);

    if (requiredFilters.length === 0) {
      return true;
    }

    const selectedFilterValues = convertFilterStringToObject(selectedFilters);
    const missingFilters = requiredFilters.filter((filter) => {
      const value = selectedFilterValues[filter.key];
      return value === undefined || value === null || value === "";
    });

    if (missingFilters.length === 0) {
      return true;
    }

    message.warning(`Please apply required filter${missingFilters.length > 1 ? "s" : ""}: ${missingFilters.map((filter) => filter.placeholder || filter.key).join(", ")}`);
    setShowFilters(true);
    return false;
  };

  const onDownloadSuccess = async (res, format) => {

    setDownloadingFormat(null);

    if (res instanceof Blob) {
      // const isFileBlob = res.type === "application/pdf" ||
      //   res.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      //   res.type === "application/octet-stream";

      // if (isFileBlob) {
      //   const fileExtension = res.type === "application/pdf" ? "pdf" : "xlsx";
      //   const fileName = `${report?.title || "report"}_${dayjs().format("YYYY-MM-DD")}.${fileExtension}`;
      //   handleDownloadFile(fileName, res);
      //   return;
      // }

      try {
        message.info("Report request submitted. Check Downloads.");
        return;

      } catch (error) {
        message.info(format === "pdf" ? "PDF report queued. Check Downloads." : "Excel report queued. Check Downloads.");
        return;
      }
    }

    message.info(format === "pdf" ? "PDF report queued. Check Downloads." : "Excel report queued. Check Downloads.");
  };

  const onDownloadError = async (err) => {
    setDownloadingFormat(null);
    if (err instanceof Blob) {
      try {
        const text = await err.text();
        const json = JSON.parse(text);
        message.error(json?.message || "Unable to generate report.");
        return;
      } catch (error) {
        message.error("Unable to generate report.");
        return;
      }
    }
    message.error("Unable to generate report.");
  };

  const goBack = () => {
    dispatch(clearFilterOptions());
    navigate(location.state?.returnTo || "/reports/all-reports", {
      state: {
        selectedReport: location.state?.selectedReport || (report ? {
          id: report.id,
          title: report.title,
          report_type_id: report.report_type_id || report.report_type?.id || null
        } : null)
      }
    })
  }

  const moveColumnChoice = (draggedName, hoveredName, dropPosition = "after") => {
    if (!draggedName || !hoveredName || draggedName === hoveredName) {
      return;
    }

    setPreviewColumns((prev) => {
      const draggedIndex = prev.findIndex((column) => column.name === draggedName);
      const hoveredIndex = prev.findIndex((column) => column.name === hoveredName);

      if (draggedIndex === -1 || hoveredIndex === -1 || draggedIndex === hoveredIndex) {
        return prev;
      }

      const nextColumns = [...prev];
      const [draggedColumn] = nextColumns.splice(draggedIndex, 1);
      const adjustedHoveredIndex = draggedIndex < hoveredIndex ? hoveredIndex - 1 : hoveredIndex;
      const insertionIndex = dropPosition === "before"
        ? adjustedHoveredIndex
        : adjustedHoveredIndex + 1;

      nextColumns.splice(insertionIndex, 0, draggedColumn);
      return nextColumns;
    });
  };

  const stopColumnChoiceAutoScroll = () => {
    columnChoiceScrollDirectionRef.current = null;
    if (columnChoiceScrollFrameRef.current) {
      cancelAnimationFrame(columnChoiceScrollFrameRef.current);
      columnChoiceScrollFrameRef.current = null;
    }
  };

  const runColumnChoiceAutoScroll = () => {
    const container = columnChoiceListRef.current;
    const direction = columnChoiceScrollDirectionRef.current;

    if (!container || !direction) {
      columnChoiceScrollFrameRef.current = null;
      return;
    }

    const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
    const nextScrollTop = Math.min(
      Math.max(container.scrollTop + (direction === "up" ? -18 : 18), 0),
      maxScrollTop
    );

    if (nextScrollTop === container.scrollTop) {
      stopColumnChoiceAutoScroll();
      return;
    }

    container.scrollTop = nextScrollTop;
    columnChoiceScrollFrameRef.current = requestAnimationFrame(runColumnChoiceAutoScroll);
  };

  const handleColumnChoiceDragHover = (clientY) => {
    const container = columnChoiceListRef.current;
    if (!container || typeof clientY !== "number") {
      stopColumnChoiceAutoScroll();
      return;
    }

    const rect = container.getBoundingClientRect();
    const edgeThreshold = Math.min(80, rect.height / 4);
    const nearTop = clientY <= rect.top + edgeThreshold;
    const nearBottom = clientY >= rect.bottom - edgeThreshold;
    const nextDirection = nearTop ? "up" : (nearBottom ? "down" : null);

    if (!nextDirection) {
      stopColumnChoiceAutoScroll();
      return;
    }

    if (columnChoiceScrollDirectionRef.current === nextDirection && columnChoiceScrollFrameRef.current) {
      return;
    }

    columnChoiceScrollDirectionRef.current = nextDirection;
    if (!columnChoiceScrollFrameRef.current) {
      columnChoiceScrollFrameRef.current = requestAnimationFrame(runColumnChoiceAutoScroll);
    }
  };

  return (
    <BodyComponent>
      <HeaderComponent modkey={ props.modkey }>
        <CreateButton onClick={ goBack } text="Back" />
      </HeaderComponent>

      <Skeleton loading={ previewLoading }>
        <div style={ { padding: 24, background: "#fff", borderRadius: 8 } }>
          <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
            <div>
              <Typography.Title level={ 4 } style={ { marginBottom: 4 } }>
                { report?.title || "Class Based Report" }
              </Typography.Title>
              <Typography.Text type="secondary">
                <b>Note</b>: The data displayed here is for preview purposes only and does not represent the complete dataset. To access the full information, please download the report in Excel or PDF format.
              </Typography.Text>
            </div>

            <Space wrap>
              <Button className="bg-primary text-light" icon={ <FilterOutlined /> } onClick={ openFilters } disabled={ !hasDirectiveFilters }>
                { hasDirectiveFilters ? "Filters" : "No Filters" }
              </Button>
              <Button icon={ <PlusOutlined /> } onClick={ openDirectiveFilterManager } disabled={ !report }>
                Manage Filters
              </Button>

              <Button icon={ <SettingOutlined /> } onClick={ () => setColumnPickerOpen(true) } disabled={ previewColumns.length === 0 }>
                Choose Columns
              </Button>
              { previewColumns.length > 0 && (
                <Tag color="blue">
                  { selectedDownloadColumns.length } / { previewColumns.length } columns selected
                </Tag>
              ) }
              <Button
                className="text-success"
                icon={ <ExceptionOutlined /> }
                loading={ downloadingFormat === "xlsx" }
                onClick={ () => openColumnPicker("xlsx") }
              >
                EXCEL
              </Button>
              <Button
                className="text-danger"
                icon={ <FilePdfOutlined /> }
                loading={ downloadingFormat === "pdf" }
                onClick={ () => openColumnPicker("pdf") }
              >
                PDF
              </Button>
              <Tooltip title="Create an email schedule for this report. Current filters will be copied if applied, and can be changed or cleared in the schedule.">
                <Button icon={ <ScheduleOutlined /> } onClick={ scheduleCurrentReport } disabled={ !report }>
                  Schedule
                </Button>
              </Tooltip>
            </Space>

            { !hasDirectiveFilters && (
              <Empty
                image={ Empty.PRESENTED_IMAGE_SIMPLE }
                description="Loading Filters..."
              />
            ) }

            { visiblePreviewColumns.length > 0 ? (
              <MUIDataTable
                title="Preview"
                data={ visiblePreviewData }
                columns={ visiblePreviewColumns }
                options={ tableOptions }
              />
            ) : (
              !loading && (
                <Empty
                  image={ Empty.PRESENTED_IMAGE_SIMPLE }
                  description={ previewLoading ? "Loading preview..." : previewColumns.length > 0 ? "Select at least one column to preview data." : "No preview data available for this report." }
                />
              )
            ) }
          </Space>
        </div>
      </Skeleton>

      { showFilters && hasDirectiveFilters && filterApiPayload && (
        <FilterComponent
          identity={ report?.id }
          filters={ filterFields }
          selected={ selectedFilters }
          api={ () => getFilterReq(filterApiPayload) }
          onFilter={ handleFilterChange }
          onCloseFilter={ closeFilters }
          module="reports"
        />
      ) }

      <Modal
        title="Manage Directive Filters"
        open={ directiveFilterModalOpen }
        onCancel={ () => {
          setDirectiveFilterModalOpen(false);
          setEditingDirectiveFilter(null);
          directiveFilterForm.resetFields();
        } }
        width={ 860 }
        footer={ [
          <Button key="close" onClick={ () => setDirectiveFilterModalOpen(false) }>
            Close
          </Button>,
          <Button key="new" icon={ <PlusOutlined /> } onClick={ startCreateDirectiveFilter }>
            New Filter
          </Button>,
          <Button key="save" type="primary" loading={ directiveFilterSaving } onClick={ submitDirectiveFilter }>
            { editingDirectiveFilter ? "Update Filter" : "Add Filter" }
          </Button>
        ] }
      >
        <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
          <Card size="small" style={ { borderRadius: 12, background: "#fafafa" } }>
            <Typography.Text>
              Add, update, or remove directive filters for this class-based report. Changes reload the filter panel and preview automatically.
            </Typography.Text>
          </Card>

          <Form form={ directiveFilterForm } layout="vertical">
            <Row gutter={ [16, 0] }>
              <Col xs={ 24 } md={ 9 }>
                <Form.Item label={ fieldLabel("group", "Filter Group") } name="group" rules={ [{ required: true, message: "Filter group is required." }] }>
                  <Select
                    showSearch
                    options={ DIRECTIVE_FILTER_GROUPS }
                    placeholder="Select filter group"
                  />
                </Form.Item>
              </Col>
              <Col xs={ 24 } md={ 6 }>
                <Form.Item label={ fieldLabel("name", "Filter Name") } name="name" rules={ [{ required: true, message: "Filter name is required." }] }>
                  <Input placeholder="Example: from, to, branch, department" />
                </Form.Item>
              </Col>
              <Col xs={ 8 } md={ 3 }>
                <Form.Item label={ fieldLabel("enable", "Enabled") } name="enable" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col xs={ 8 } md={ 3 }>
                <Form.Item label={ fieldLabel("required", "Required") } name="required" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
              <Col xs={ 8 } md={ 3 }>
                <Form.Item label={ fieldLabel("multiple", "Multiple") } name="multiple" valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Row gutter={ [12, 12] }>
            { directiveFilters.map((filter) => {
              const config = normalizeDirectiveConfig(filter.config);
              return (
                <Col xs={ 24 } md={ 12 } key={ filter.id }>
                  <Card size="small" style={ { borderRadius: 12 } }>
                    <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                      <Space wrap>
                        <Tag color="blue">{ filter.name }</Tag>
                        <Tag>{ filter.group }</Tag>
                        { config.required && <Tag color="red">Required</Tag> }
                        { config.multiple && <Tag color="geekblue">Multiple</Tag> }
                        { config.enable === false && <Tag color="default">Disabled</Tag> }
                      </Space>
                      <Space>
                        <Button size="small" icon={ <EditOutlined /> } onClick={ () => startEditDirectiveFilter(filter) }>
                          Edit
                        </Button>
                        <Popconfirm
                          title="Remove this directive filter?"
                          okText="Remove"
                          okButtonProps={ { danger: true } }
                          onConfirm={ () => deleteDirectiveFilter(filter) }
                        >
                          <Button size="small" danger icon={ <DeleteOutlined /> }>
                            Remove
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Space>
                  </Card>
                </Col>
              );
            }) }
          </Row>

          { directiveFilters.length === 0 && (
            <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No directive filters configured yet." />
          ) }
        </Space>
      </Modal>

      <Modal
        title="Choose Report Columns"
        open={ columnPickerOpen }
        onCancel={ () => {
          setColumnPickerOpen(false);
          setPendingDownloadFormat(null);
          setColumnSearch("");
        } }
        width={ 760 }
        footer={ [
          <Button
            key="select-all"
            onClick={ () => setSelectedDownloadColumns(previewColumns.map((column) => column.name)) }
            disabled={ previewColumns.length === 0 }
          >
            Select All
          </Button>,
          <Button
            key="clear-all"
            danger
            onClick={ () => setSelectedDownloadColumns([]) }
            disabled={ previewColumns.length === 0 }
          >
            Clear
          </Button>,
          <Button
            key="cancel"
            onClick={ () => {
              setColumnPickerOpen(false);
              setPendingDownloadFormat(null);
              setColumnSearch("");
            } }
          >
            Cancel
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={ pendingDownloadFormat === "pdf" ? <FilePdfOutlined /> : <ExceptionOutlined /> }
            disabled={ selectedDownloadColumns.length < 1 }
            onClick={ () => downloadClassBasedReport(pendingDownloadFormat || "xlsx") }
          >
            Download { (pendingDownloadFormat || "xlsx").toUpperCase() }
          </Button>
        ] }
      >
        <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
          <Card size="small" style={ { borderRadius: 12, background: "#fafafa" } }>
            <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
              <Typography.Text strong>
                Pick only the columns you want in the downloaded report, then drag them into your preferred order.
              </Typography.Text>
              <Space wrap>
                <Tag color="blue">{ selectedDownloadColumns.length } selected</Tag>
                <Tag>{ previewColumns.length } available</Tag>
                { pendingDownloadFormat && <Tag color="purple">{ pendingDownloadFormat.toUpperCase() }</Tag> }
              </Space>
              <Space>
                <Button
                  key="download"
                  type="primary"
                  icon={ pendingDownloadFormat === "pdf" ? <FilePdfOutlined /> : <ExceptionOutlined /> }
                  disabled={ selectedDownloadColumns.length < 1 }
                  onClick={ () => downloadClassBasedReport(pendingDownloadFormat || "xlsx") }
                >
                  Download { (pendingDownloadFormat || "xlsx").toUpperCase() }
                </Button>
              </Space>
            </Space>
          </Card>

          <Input
            allowClear
            value={ columnSearch }
            onChange={ (event) => setColumnSearch(event.target.value) }
            prefix={ <SearchOutlined /> }
            placeholder="Search columns"
          />

          <Checkbox
            indeterminate={ selectedDownloadColumns.length > 0 && selectedDownloadColumns.length < previewColumns.length }
            checked={ previewColumns.length > 0 && selectedDownloadColumns.length === previewColumns.length }
            onChange={ (event) => {
              setSelectedDownloadColumns(event.target.checked ? previewColumns.map((column) => column.name) : []);
            } }
          >
            Select all columns
          </Checkbox>

          <div
            ref={ columnChoiceListRef }
            style={ {
              maxHeight: 420,
              overflowY: "auto",
              paddingRight: 4
            } }
          >
            <Row gutter={ [12, 12] }>
              { filteredColumnChoices.map((column) => (
                <Col span={ 24 } key={ column.name }>
                  <SortableColumnChoice
                    column={ column }
                    isSelected={ selectedDownloadColumns.includes(column.name) }
                    onToggle={ () => toggleColumnSelection(column.name, setSelectedDownloadColumns) }
                    onMove={ moveColumnChoice }
                    onDragHover={ handleColumnChoiceDragHover }
                    onDragEnd={ stopColumnChoiceAutoScroll }
                  />
                </Col>
              )) }
            </Row>
          </div>

          { filteredColumnChoices.length === 0 && (
            <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No columns match your search." />
          ) }
        </Space>
      </Modal>
      { scheduleModal }
    </BodyComponent>
  );
};

function capitalizeString(inputString) {
  return inputString.charAt(0).toUpperCase() + inputString.slice(1);
}

function isValidJSON(value) {
  if (typeof value !== "string") return false;

  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    return false;
  }
}

function arrangeItems(input) {
  if (!input.trim()) {
    return "";
  }

  const groups = input.split(";");
  const groupedItems = {};

  groups.forEach((group) => {
    const [fieldType, items] = group.split("_|");
    if (!fieldType || !items) {
      return;
    }

    if (!groupedItems[fieldType]) {
      groupedItems[fieldType] = [];
    }

    groupedItems[fieldType] = groupedItems[fieldType].concat(items.split(","));
  });

  return Object.keys(groupedItems)
    .map((fieldType) => `${fieldType}_|${groupedItems[fieldType].sort().join(",")}`)
    .join(";");
}

function convertFilterStringToObject(filterString) {
  if (!filterString) {
    return {};
  }

  return filterString
    .split(";")
    .filter(Boolean)
    .reduce((acc, pair) => {
      const [key, value = ""] = pair.split("_|");
      if (!key) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {});
}

function toggleColumnSelection(columnName, setSelectedDownloadColumns) {
  setSelectedDownloadColumns((prev) =>
    prev.includes(columnName)
      ? prev.filter((item) => item !== columnName)
      : [...prev, columnName]
  );
}

const COLUMN_CHOICE_TYPE = "report-column-choice";

function SortableColumnChoice({ column, isSelected, onToggle, onMove, onDragHover, onDragEnd }) {
  const ref = useRef(null);
  const [dropPosition, setDropPosition] = useState(null);

  const [{ isDragging }, drag] = useDrag(() => ({
    type: COLUMN_CHOICE_TYPE,
    item: { name: column.name },
    end: () => {
      onDragEnd?.();
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }), [column.name, onDragEnd]);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: COLUMN_CHOICE_TYPE,
    hover: (draggedItem, monitor) => {
      if (!ref.current || draggedItem.name === column.name) {
        setDropPosition(null);
        return;
      }

      const rect = ref.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        onDragEnd?.();
        setDropPosition(null);
        return;
      }

      onDragHover?.(clientOffset.y);

      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const hoverClientY = clientOffset.y - rect.top;

      setDropPosition(hoverClientY < hoverMiddleY ? "before" : "after");
    },
    drop: (draggedItem) => {
      if (draggedItem.name === column.name) {
        return undefined;
      }

      onMove(draggedItem.name, column.name, dropPosition || "after");
      onDragEnd?.();
      setDropPosition(null);
      return { moved: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [column.name, dropPosition, onDragEnd, onDragHover, onMove]);

  drag(drop(ref));

  useEffect(() => {
    if (!isOver && dropPosition !== null) {
      setDropPosition(null);
    }
  }, [dropPosition, isOver]);

  useEffect(() => {
    if (!isOver) {
      onDragEnd?.();
    }
  }, [isOver, onDragEnd]);

  const showDropIndicator = isOver && canDrop && dropPosition;
  const indicatorStyle = dropPosition === "before"
    ? { boxShadow: "inset 0 5px 0 #52c41a" }
    : { boxShadow: "inset 0 -5px 0 #52c41a" };

  return (
    <Card
      ref={ ref }
      size="small"
      hoverable
      onClick={ onToggle }
      style={ {
        borderRadius: 12,
        borderWidth: showDropIndicator ? 2 : 1,
        borderStyle: showDropIndicator ? "dashed" : "solid",
        borderColor: showDropIndicator ? "#52c41a" : (isSelected ? "#1677ff" : "#f0f0f0"),
        background: showDropIndicator ? "#eaffd6" : (isSelected ? "#f6fbff" : "#fff"),
        cursor: "move",
        opacity: isDragging ? 0.55 : 1,
        transition: "background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
        ...((showDropIndicator && indicatorStyle) || {})
      } }
    >
      <Space align="start" style={ { width: "100%", justifyContent: "space-between" } }>
        <Space align="start">
          <Checkbox
            checked={ isSelected }
            onChange={ onToggle }
            onClick={ (event) => event.stopPropagation() }
          />
          <Space direction="vertical" size={ 2 }>
            <Typography.Text strong>{ column.label }</Typography.Text>
            <Typography.Text type="secondary">{ column.name }</Typography.Text>
          </Space>
        </Space>
        <HolderOutlined style={ { color: "#8c8c8c", fontSize: 16, marginTop: 4 } } />
      </Space>
      { showDropIndicator && (
        <Typography.Text
          strong
          style={ {
            display: "block",
            marginTop: 10,
            color: "#389e0d"
          } }
        >
          Drop here
        </Typography.Text>
      ) }
    </Card>
  );
}

function filterEmptyTableRows(rows) {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.filter((row) => {
    if (Array.isArray(row)) {
      return row.length > 0 && row.some((cell) => cell !== null && cell !== undefined && cell !== "");
    }

    if (row && typeof row === "object") {
      const values = Object.values(row);
      return values.length > 0 && values.some((cell) => cell !== null && cell !== undefined && cell !== "");
    }

    return row !== null && row !== undefined && row !== "";
  });
}

function normalizeDirectiveConfig(config) {
  if (!config) {
    return {};
  }

  if (typeof config === "string") {
    try {
      return JSON.parse(config);
    } catch (error) {
      return {};
    }
  }

  return typeof config === "object" ? config : {};
}

const DIRECTIVE_FILTER_GROUPS = [
  { value: "date_time_fields", label: "Date / Time Fields" },
  { value: "organization_structure_fields", label: "Organization Structure Fields" },
  { value: "supporting_fields", label: "Supporting Fields" },
  { value: "employee_fields", label: "Employee Fields" },
  { value: "employment_fields", label: "Employment Fields" },
  { value: "payroll_fields", label: "Payroll Fields" },

  { value: "leave_fields", label: "Leave Fields" },
  { value: "custom_fields", label: "Custom Fields" }
];

export default GenerateClassBasedReport;
